// AdminBotDetail.tsx — /admin/bots/:id (Task 3 of the admin-v2 cycle, spec
// docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md §B2). Bot facts,
// sources table, recent jobs (best-effort linked via `Job.payload`'s botId/sourceId server-
// side), and "Open owner's view" — impersonates the workspace owner then hard-navigates to
// the owner-facing bot route (`/bots/:botId`, see App.tsx) via the shared
// `../lib/admin-impersonate.ts` helper (extracted from AdminWorkspaces.tsx's "View as" flow).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { AdminBotDetailDto } from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import { Card } from '@repo/ui/components/card';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { StatusChip, type StatusChipState } from '@repo/ui/components/status-chip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { impersonateWorkspaceOwner } from '../lib/admin-impersonate';
import { apiFetch } from '../lib/api';
import { relativeTime } from '../lib/relative-time';
import { useLocale } from '../locales/context';
import { formatDate as formatDateIntl, formatDateTime as formatDateTimeIntl, formatNumber } from '../locales/format';
import type { Locale } from '../locales';

/** Truncate a long id to a readable prefix; the full value stays available via `title`
 * (same idiom as AdminBots.tsx's `truncateId`). */
function truncateId(id: string, head = 12): string {
  return id.length > head ? `${id.slice(0, head)}…` : id;
}

/** Truncated cell with the full value on `title` (same idiom as AdminJobs.tsx). */
function TruncatedCell({ value, max = 60 }: { value: string; max?: number }) {
  const truncated = value.length > max ? `${value.slice(0, max)}…` : value;
  return (
    <span className="block max-w-80 truncate" title={value}>
      {truncated}
    </span>
  );
}

/** Sized to the real facts header + sources table below (handoff Shell Anatomy §06). */
function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <SkeletonText className="w-28" />
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-48" />
          <SkeletonText className="w-16" />
        </div>
        <SkeletonText className="w-80" />
      </div>
      <div className="flex flex-col gap-2">
        <SkeletonText className="w-16" />
        <div className="flex flex-col overflow-hidden rounded-lg border">
          <div className="h-[34px] bg-muted" />
          {['w-[85%]', 'w-[65%]'].map((w, i) => (
            <div key={w} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
              <SkeletonText className={w} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string, locale: Locale): string {
  return formatDateIntl(iso, locale, { month: 'long', day: 'numeric', year: 'numeric' });
}

export function AdminBotDetail() {
  const { t, locale } = useLocale();
  const { id = '' } = useParams<{ id: string }>();

  const { data: bot, isPending, error } = useQuery({
    queryKey: ['admin', 'bot', id],
    queryFn: () => apiFetch<AdminBotDetailDto>(`/api/admin/bots/${id}`),
  });

  const [openViewPending, setOpenViewPending] = useState(false);
  const [openViewError, setOpenViewError] = useState<string | null>(null);

  async function handleOpenOwnerView() {
    if (!bot) return;
    setOpenViewError(null);
    setOpenViewPending(true);
    try {
      await impersonateWorkspaceOwner(bot.workspaceId, t, `/bots/${bot.id}`);
    } catch (err) {
      setOpenViewError(
        err instanceof Error ? err.message : t.admin.bots.openOwnerViewErrorFallback,
      );
      setOpenViewPending(false);
    }
  }

  if (isPending) return <DetailSkeleton />;
  if (error) return <p className="text-destructive">{error.message}</p>;
  if (!bot) return null;

  return (
    <div className="flex flex-col gap-6">
      <Link to="/admin/bots" className="w-fit text-sm text-muted-foreground hover:text-foreground">
        {t.admin.bots.backToBots}
      </Link>

      {/* Facts */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-display text-2xl font-bold">{bot.name}</h1>
          <StatusChip state={bot.status === 'active' ? 'ready' : 'paused'} label={bot.status} />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="font-mono text-xs">{bot.publicId}</span>
          <span>
            {t.admin.bots.workspaceLabel}
            <Link to={`/admin/workspaces/${bot.workspaceId}`} className="hover:underline">
              {bot.workspaceName}
            </Link>
          </span>
          <span title={bot.aiSearchInstanceId ?? undefined} className="font-mono text-xs">
            {t.admin.bots.instanceLabel({
              id: bot.aiSearchInstanceId ? truncateId(bot.aiSearchInstanceId) : '—',
            })}
          </span>
          <span>{t.admin.bots.createdLabel({ date: formatDate(bot.createdAt, locale) })}</span>
        </div>
        <div className="flex flex-col items-start gap-1">
          <Button type="button" variant="outline" size="sm" disabled={openViewPending} onClick={handleOpenOwnerView}>
            {openViewPending ? t.admin.bots.openingCta : t.admin.bots.openOwnerViewCta}
          </Button>
          {openViewError && <p className="text-[13px] text-destructive">{openViewError}</p>}
        </div>
      </div>

      {/* Sources table */}
      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.bots.sourcesHeading}</h2>
        {bot.sources.length === 0 ? (
          <p className="text-muted-foreground">{t.admin.bots.noSourcesYet}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.admin.bots.colUrl}</TableHead>
                  <TableHead>{t.admin.bots.colType}</TableHead>
                  <TableHead>{t.admin.bots.colSourceStatus}</TableHead>
                  <TableHead>{t.admin.bots.colPages}</TableHead>
                  <TableHead>{t.admin.bots.colLastCrawled}</TableHead>
                  <TableHead>{t.admin.bots.colError}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bot.sources.map((source) => (
                  <TableRow key={source.id}>
                    <TableCell>
                      <TruncatedCell value={source.uri} />
                    </TableCell>
                    <TableCell>{source.type}</TableCell>
                    <TableCell>
                      <StatusChip state={source.status as StatusChipState} label={source.status} />
                    </TableCell>
                    <TableCell>{formatNumber(source.pageCount, locale)}</TableCell>
                    <TableCell>{relativeTime(source.lastCrawledAt, t.time)}</TableCell>
                    <TableCell>
                      {source.error ? <TruncatedCell value={source.error} /> : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Recent jobs */}
      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.bots.recentJobsHeading}</h2>
        {bot.recentJobs.length === 0 ? (
          <p className="text-muted-foreground">{t.admin.bots.noJobsLinked}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.admin.bots.colKind}</TableHead>
                  <TableHead>{t.admin.bots.colJobStatus}</TableHead>
                  <TableHead>{t.admin.bots.colAttempts}</TableHead>
                  <TableHead>{t.admin.bots.colUpdated}</TableHead>
                  <TableHead>{t.admin.bots.colPayload}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bot.recentJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-medium">
                      <Link to={`/admin/jobs/${job.id}`} className="hover:underline">
                        {job.kind}
                      </Link>
                    </TableCell>
                    <TableCell>{job.status}</TableCell>
                    <TableCell>{job.attempts}</TableCell>
                    <TableCell>{formatDateTimeIntl(job.updatedAt, locale)}</TableCell>
                    <TableCell>
                      <TruncatedCell value={job.payloadSummary} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Card className="p-4 text-xs text-muted-foreground">{t.admin.bots.recentJobsFootnote}</Card>
    </div>
  );
}
